import { dialog } from "nq-component";
import BaseListPresenter from "../../../../../../base/BaseListPresenter";
import { formatDate } from "../../../../../../components/common/Converter";
import {
  findObjectUseCase,
  upsertUseCase,
} from "../../../../../../usecases/object";
import * as XLSX from "xlsx";
import PrintServiceRecord from "./PrintServiceRecord/PrintServiceRecord";
import { renderToStaticMarkup } from "react-dom/server";

export default class ServiceRecordPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.limit = 10;
    const email = this.view.getCurrent();
    this.where = { empID: email };
    this.search = {};
    this.filter = {};
    this.include = ["employee", "timeRecStats", "all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { from: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  async handlePrintData() {
    // printComponent(this.contractPDF.current, "Tasks");
    // console.log("OBEJCTS: ", this.objects);
    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      { where: this.where }
    );

    const remarks = await findObjectUseCase().execute("remarks_page", {
      where: this.where,
      sort: { created: 1 },
    });
    const iframe = document.createElement("iframe");
    iframe.style.position = "absolute";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    const printContentElement = PrintServiceRecord(
      objects,
      remarks,
      this.view.props.object
    );
    const printContentHTML = renderToStaticMarkup(printContentElement);

    iframeDoc.open();
    iframeDoc.write(
      `<html><head><title>Print DTR</title><style>
      @page {
        margin: 1mm;
      }
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 0;
        font-size: 14px;
      }
      .print-content-container {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
      }
      .print-content {
        width: 100%;
        max-width: 100%;
        margin: 0;
        padding: 10px;
      }
      @media print {
        th {
          font-weight: normal; 
        }
      }
    </style></head><body>`
    );

    iframeDoc.write(printContentHTML);

    // Adding the script for the onload callback function
    iframeDoc.write(`<script>
        window.print();
        window.close();
        </script>`);

    iframeDoc.write("</body></html>");
    iframeDoc.close();
  }

  async getGenerateCSV() {
    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      { where: this.where }
    );
    let csvContent =
      "\uFEFFFROM,TO,DESIGNATION,STATUS,SALARY,STATION,BRANCH,LEAVE,DATE,CAUSE\n"; // CSV header with BOM for UTF-8

    const escapeCsvValue = (value) => {
      if (typeof value === "string" && value.includes(",")) {
        return `"${value}"`;
      }
      return value;
    };

    objects.forEach((item) => {
      const from = escapeCsvValue(formatDate(item?.from) || "");
      const to = escapeCsvValue(formatDate(item?.to) || "");
      const designation = escapeCsvValue(item?.designation || "");
      const status = escapeCsvValue(item?.status || "");
      const salary = escapeCsvValue(item?.salary || "");
      const station = escapeCsvValue(item?.station || "");
      const branch = escapeCsvValue(item?.branch || "");
      const leave = item?.leave?.join("; ") || ""; // Combine all leave entries into one string
      const date = escapeCsvValue(formatDate(item?.date) || "");
      const cause = escapeCsvValue(item?.cause || "");
      csvContent += `${from},${to},${designation},${status},${salary},${station},${branch},${leave},${date},${cause}\n`;
    });

    // Create a Blob with UTF-8 encoding
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Service Record.csv`);
    document.body.appendChild(link);
    link.click();
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  handleConfirmBtn(data) {
    data.employee = { id: data.employee.id };
    upsertUseCase().execute(this.view.getCollectionName(), data);
    dialog.close();
    this.view.showSuccess("Cause updated.");
    this.view.refetchData();
    try {
    } catch (e) {
      console.error("Error: ", e);
    }
  }

  parseExcelDateForInput(serial) {
    if (typeof serial !== "number" || isNaN(serial) || serial <= 0) {
      return ""; // return empty string for invalid date
    }

    const utcDays = Math.floor(serial - 25569); // Excel base offset
    const utcValue = utcDays * 86400; // seconds since Jan 1, 1970
    const date = new Date(utcValue * 1000);

    if (isNaN(date.getTime())) {
      return "";
    }

    // Format to YYYY-MM-DD for input[type="date"]
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  importDataIntoCSV(event, object) {
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const rawExcelData = jsonData.map((item) => {
        item.from = this.parseExcelDateForInput(item.from);
        item.to = this.parseExcelDateForInput(item.to);
        item.employee = { id: object?.id };
        item.empID = object?.id;
        item.email = object?.email;
        item.leave = item?.leave?.split(", ");
        return item;
      });
      this.view.setState({ rawExcelData });
    };

    reader.readAsArrayBuffer(file);
  }

  handleImportData(rawExcelData) {
    try {
      rawExcelData.forEach(async (item) => {
        await upsertUseCase().execute(this.view.getCollectionName(), item);
      });
      this.view.refetchData();
      this.view.showSuccess("Data imported");
      this.view.setState({ rawExcelData: [] });
      this.view.fileInputRef.current.value = "";
    } catch (e) {
      console.error("Error: ", e);
    }
  }
}
